import { useRef, useState } from "react";
import toast from "react-hot-toast";
import { BsCloudUpload } from "react-icons/bs";
import PopupModal from "../../PopupModal";
import LoadingView from "../../LoadingView";
import { PrimaryButton, SecondaryButton } from "../../../StyledCss/styledbtn";
import { parseHiringResume } from "../../../api/services/hroneApi";
import { normalizeParsedCandidate } from "../../../util/hiringUtils";
import "./index.css";

const RESUME_ACCEPT = {
  accept: ".pdf",
  extensions: ["pdf"],
};

const UploadResumeModal = ({ jobId, isOpen, onClose, onParsed }) => {
  const orgDetails   = JSON.parse(localStorage.getItem("org"));
  const primaryColor = orgDetails?.primaryColor || "#e86020";

  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isParsing, setIsParsing] = useState(false);

  const reset = () => {
    setSelectedFile(null);
    setIsDragging(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleClose = () => {
    if (isParsing) return;
    reset();
    onClose();
  };

  const validateFile = (file) => {
    if (!file) return false;
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!RESUME_ACCEPT.extensions.includes(ext)) {
      toast.error("Unsupported file. Please upload a PDF.");
      return false;
    }
    return true;
  };

  const handleFileSelect = (file) => {
    if (!validateFile(file)) return;
    setSelectedFile(file);
  };

  const handleInputChange = (e) => {
    handleFileSelect(e.target.files?.[0]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (isParsing) return;
    const file = e.dataTransfer.files?.[0];
    handleFileSelect(file);
  };

  const handleParse = async () => {
    if (!selectedFile || isParsing) return;

    const fd = new FormData();
    fd.append("resume", selectedFile, selectedFile.name);

    setIsParsing(true);
    try {
      const res  = await parseHiringResume(jobId, fd);
      const data = await res.json();
      if (!data.status) {
        throw new Error(data.message || data.detail || "Parsing failed");
      }
      const parsed = normalizeParsedCandidate(data.data || {});
      onParsed({
        ...parsed,
        resumeFile:      selectedFile,
        localPreviewUrl: selectedFile.type === "application/pdf"
          ? URL.createObjectURL(selectedFile)
          : parsed.resumeUrl || "",
      });
      reset();
      onClose();
    } catch (err) {
      if (!navigator.onLine) {
        toast.error("Network error. Please check your connection.");
      } else {
        toast.error(err.message || "Parsing failed. Please try again.");
      }
    } finally {
      setIsParsing(false);
    }
  };

  return (
    <PopupModal
      showModal={isOpen}
      modalName="Upload Resume"
      closeBtn={handleClose}
      width="520px"
    >
      <div className="upload-resume-modal" style={{ "--hiring-primary": primaryColor }}>
        {isParsing ? (
          <div className="upload-resume-loading">
            <LoadingView />
            <p>Parsing resume…</p>
          </div>
        ) : (
          <>
            <p className="upload-resume-hint">
              Upload a resume to extract candidate details using AI. Supported format: PDF.
            </p>

            <div
              className={`upload-resume-dropzone${isDragging ? " upload-resume-dropzone--active" : ""}`}
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
            >
              <input
                ref={inputRef}
                type="file"
                accept={RESUME_ACCEPT.accept}
                style={{ display: "none" }}
                onChange={handleInputChange}
              />
              <BsCloudUpload className="upload-resume-icon" />
              <p className="upload-resume-drop-title">
                {selectedFile ? selectedFile.name : "Drag & drop resume here"}
              </p>
              <p className="upload-resume-drop-sub">
                {selectedFile ? "Click to replace file" : "or click to browse"}
              </p>
            </div>

            <div className="upload-resume-actions">
              <SecondaryButton type="button" onClick={handleClose}>Cancel</SecondaryButton>
              <PrimaryButton type="button" disabled={!selectedFile} onClick={handleParse}>
                Parse Resume
              </PrimaryButton>
            </div>
          </>
        )}
      </div>
    </PopupModal>
  );
};

export default UploadResumeModal;
